import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import addDays from 'date-fns/add_days';
import { flatMap as _flatMap, get as _get, has as _has } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, mergeMap, take, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import {
  AddVehicleCoverage,
  addVehicleInfoQuery,
  AddVehicleNotificationRequestAgentEmail,
  AddVehicleRateRequest,
  ANSRequest,
  typeOfAgentEmail,
  vehicleCoverageSelectionOptions,
  vehiclePrimaryUse
} from '@amfam/policy/add-vehicle/data-access';
import { PlautoService, PolicySelectors as policyQuery } from '@amfam/policy/data-access';
import { AutoPolicy, RiskModel } from '@amfam/policy/models';
import { PrettyPolicyNum, TermPipe } from '@amfam/policy/pipes';
import { VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { NewVehicle } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade, EventAnalyticParams, PageAnalyticParams } from '@amfam/shared/analytics';
import { QuoteSummaryModel } from '@amfam/shared/models';
import { CoverageCurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { Agent } from '@amfam/shared/utility/agent';
import {
  CopyService,
  EnvironmentBase,
  MessageDetail,
  TemplatedEmail,
  UtilService
} from '@amfam/shared/utility/shared-services';

import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { AddVehicleAnalyticsModel } from '../models/add-vehicle-analytics.model';
import { CoveragesModel } from '../models/coverages.model';
import {
  CoverageDescriptionModel,
  DriverInfoModel,
  PrimaryUsageInfo,
  VehicleFeaturesInfoModel,
  VehicleInfo,
  VehicleUnderwritingPartnerQuestionsModels,
  VehicleUnderwritingQuestionsModels
} from '../models/vehicle-info.model';

@Injectable({
  providedIn: 'root'
})
export class AddVehicleFeatureService {
  environment: EnvironmentBase;
  constructor(
    private store: Store,
    private copyService: CopyService,
    private utilService: UtilService,
    private analyticsFacade: AnalyticsFacade,
    protected autoPolicyService: PlautoService,
    private vehicleCoveragesFacade: VehicleCoveragesFacade
  ) {}

  public buildANSNotificationPayload(
    policyNumber: string,
    notificationText: string,
    orderNumber: string,
    agent: Agent,
    notificationType: string,
    customerName: string
  ) {
    const ansNotificationPayload: ANSRequest = {
      notificationsRequest: {
        notificationText,
        notificationType,
        customerName,
        referenceNumber: orderNumber || 'N/A',
        formattedReferenceNumber: new PrettyPolicyNum().transform(policyNumber),
        policyNumber,
        from: 'DMSI',
        dueDate: addDays(new Date(), 2).toISOString(),
        createdBy: 'DMSI',
        uniqueId: this.utilService.generateId(),
        sourceSystem: 'MyAccountWebApplication'
      },
      producerId: agent.id
    };

    return ansNotificationPayload;
  }

  public getANSText(
    typeOfEmail: typeOfAgentEmail,
    agentEmailNotificationPayload: AddVehicleNotificationRequestAgentEmail
  ) {
    return this.copyService.getCopy(`policy.${typeOfEmail}AgentANS`, {
      ...agentEmailNotificationPayload
    });
  }

  public getANSCode(
    typeOfEmail: typeOfAgentEmail,
    agentEmailNotificationPayload: AddVehicleNotificationRequestAgentEmail
  ) {
    return this.copyService.getCopy(`policy.${typeOfEmail}ANSCode`, {
      ...agentEmailNotificationPayload
    });
  }

  public buildTemplatedCustomerEmail(
    customerId: string,
    customerEmailAddress: string,
    customerFirstName: string,
    partnerId: string,
    typeOfEmail: typeOfAgentEmail,
    agent: Agent,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: number,
    newTermDescription: number,
    drivers: string,
    garagedAddress: string,
    coverages: string,
    fees: string,
    discounts: string
  ) {
    const email = new TemplatedEmail(
      this.copyService.getCopy(`policy.${typeOfEmail}templateID`),
      partnerId,
      'MyAccount',
      'EN',
      customerId,
      customerEmailAddress,
      this.buildCustomerMessageDetails(
        customerEmailAddress,
        customerFirstName,
        agent.id,
        policyNumber,
        newRiskInformation,
        newTotalPremium,
        newMonthlyPremium,
        newTermDescription,
        drivers,
        garagedAddress,
        coverages,
        fees,
        discounts
      )
    );
    return email;
  }

  public buildTemplatedPartnerCustomerEmail(
    customerId: string,
    customerEmailAddress: string,
    customerFirstName: string,
    partnerId: string,
    accountId: string,
    typeOfEmail: typeOfAgentEmail,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: string,
    newTermDescription: number,
    drivers: string,
    garagedAddress: string,
    coverages: string,
    fees: string,
    discounts: string
  ) {
    const email = new TemplatedEmail(
      this.copyService.getCopy(`policy.${typeOfEmail}templateID`),
      partnerId,
      'MyAccount',
      'EN',
      customerId,
      customerEmailAddress,
      this.buildPartnerCustomerMessageDetails(
        customerEmailAddress,
        customerFirstName,
        policyNumber,
        newRiskInformation,
        newTotalPremium,
        newMonthlyPremium,
        newTermDescription,
        drivers,
        garagedAddress,
        coverages,
        fees,
        discounts
      ),
      accountId
    );
    return email;
  }

  public buildTemplatedCustomerEmailAfterBind(
    customerId: string,
    customerEmailAddress: string,
    customerFirstName: string,
    partnerId: string,
    typeOfEmail: typeOfAgentEmail,
    agent: Agent,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: number,
    newTermDescription: number,
    vehicleCoverages: Map<string, string>,
    policyCoverages: Map<string, string>,
    discounts: Map<string, string>,
    futurePayments: Map<string, string>,
    vehicleCount: number
  ) {
    const email = new TemplatedEmail(
      env.environment === 'prod'
        ? this.copyService.getCopy(`policy.${typeOfEmail}templateIDAFI`)
        : this.copyService.getCopy(`policy.${typeOfEmail}templateIDAFITest`),
      partnerId,
      'MyAccount',
      'EN',
      customerId,
      customerEmailAddress,
      this.buildCustomerMessageDetailsAfterBind(
        customerEmailAddress,
        customerFirstName,
        agent.id,
        policyNumber,
        newRiskInformation,
        newTotalPremium,
        newMonthlyPremium,
        newTermDescription,
        vehicleCoverages,
        policyCoverages,
        discounts,
        futurePayments,
        vehicleCount
      )
    );
    return email;
  }

  public getFeesString(
    fees: {
      name: string;
      amount: number;
    }[]
  ) {
    return _flatMap(fees, fee => fee.name).join(', ');
  }

  public getPrimaryDriversNames(driverList: DriverInfoModel[]) {
    return _flatMap(driverList, driver => {
      if (driver.primary) {
        return _get(driver, 'name', '').split(' ')[0];
      }
    })
      .filter(driver => !!driver)
      .join(', ');
  }

  public getDiscountsString(discountsList: { discountName: string; discountCode: string }[]) {
    return _flatMap(discountsList, discount => discount.discountName).join(', ');
  }

  public getCoveragesString(coverageList: AddVehicleCoverage[]) {
    return _flatMap(coverageList, coverage => (coverage.selected ? coverage.coverageName : ''))
      .filter(coverage => !!coverage)
      .join(', ');
  }

  hasDisqualifyingAnswers(): Observable<boolean> {
    return this.store.select(addVehicleFeatureSelector.selectCurrentVehicleInfo).pipe(
      withLatestFrom(this.store.select(policyQuery.selectAdvanceAutoPolicies)),
      take(1),
      map(
        ([vehicleStateInfo, advancedAutoPolicies]) =>
          this.garageAddressHardStopCheck(vehicleStateInfo.garageAddress) ||
          this.underWritingHardStopCheck(vehicleStateInfo.underwritingQuestions) ||
          this.primaryUseHardStopCheck(vehicleStateInfo.primaryUsageInfo) ||
          this.garageAddressWithSingleAdvancedPolicy(vehicleStateInfo, advancedAutoPolicies)
      )
    );
  }

  hasUWHardStop(): Observable<boolean> {
    return this.store.select(addVehicleFeatureSelector.selectCurrentVehicleInfo).pipe(
      withLatestFrom(this.store.select(policyQuery.selectAdvanceAutoPolicies)),
      take(1),
      map(
        ([vehicleStateInfo, advancedAutoPolicies]) =>
          vehicleStateInfo.garageAddress ===
            this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText') ||
          _get(vehicleStateInfo, 'underwritingQuestions.hasOtherOrCoowner', false) ||
          _get(vehicleStateInfo, 'underwritingQuestions.usedForBusiness', false) ||
          _get(vehicleStateInfo, 'underwritingQuestions.isDamaged', false) ||
          _get(vehicleStateInfo, 'underwritingQuestions.kitCarReplica', false) ||
          this.primaryUseHardStopCheckForPartners(vehicleStateInfo.primaryUsageInfo) ||
          this.garageAddressWithSingleAdvancedPolicy(
            vehicleStateInfo,
            advancedAutoPolicies as AutoPolicy[]
          )
      )
    );
  }

  public underWritingHardStopCheck(
    underWritingQuestions:
      | VehicleUnderwritingQuestionsModels
      | VehicleUnderwritingPartnerQuestionsModels
  ) {
    return (
      _get(underWritingQuestions, 'hasOtherOrCoowner') ||
      _get(underWritingQuestions, 'usedForBusiness') ||
      _get(underWritingQuestions, 'kitCarReplica')
    );
  }

  public primaryUseHardStopCheck(primaryUsageInfo: PrimaryUsageInfo) {
    const primaryUse = _get(primaryUsageInfo, 'primaryUse');
    return (
      vehiclePrimaryUse[primaryUse] === vehiclePrimaryUse.farming ||
      vehiclePrimaryUse[primaryUse] === vehiclePrimaryUse.business ||
      vehiclePrimaryUse[primaryUse] === vehiclePrimaryUse.antique
    );
  }

  public primaryUseHardStopCheckForPartners(primaryUsageInfo: PrimaryUsageInfo) {
    const primaryUse = _get(primaryUsageInfo, 'primaryUse');
    return (
      vehiclePrimaryUse[primaryUse] === vehiclePrimaryUse.farming ||
      vehiclePrimaryUse[primaryUse] === vehiclePrimaryUse.business
    );
  }

  public garageAddressWithMultipleAdvancedPolicies(
    vehicleDetailsState: VehicleInfo,
    advancedAutoPolicies: AutoPolicy[]
  ) {
    return (
      vehicleDetailsState?.garageAddress ===
        this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText') &&
      _get(advancedAutoPolicies, 'length', 0) > 1
    );
  }

  public garageAddressWithSingleAdvancedPolicy(
    vehicleDetailsState: VehicleInfo,
    advancedAutoPolicies: AutoPolicy[]
  ) {
    return (
      vehicleDetailsState?.garageAddress ===
        this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText') &&
      _get(advancedAutoPolicies, 'length', 0) === 1
    );
  }

  typeOfAgentEmailBeingSent(
    isAddVehicleTransActionCancelled?: boolean
  ): Observable<typeOfAgentEmail> {
    if (isAddVehicleTransActionCancelled) {
      return of(typeOfAgentEmail.vehicleCancelledTransaction);
    }

    return this.store.select(addVehicleFeatureSelector.selectCurrentVehicleInfo).pipe(
      withLatestFrom(
        this.store.select(policyQuery.selectAdvanceAutoPolicies),
        this.store.select(addVehicleInfoQuery.selectNewVehicleDrivers),
        this.store.select(addVehicleInfoQuery.selectNewVehicleList)
      ),
      mergeMap(([vehicleDetailsState, advancedAutoPolicies, driversList, vehicleList]) => {
        if (
          !_get(vehicleDetailsState, 'primaryUsageInfo.primaryUse') &&
          !!_get(vehicleDetailsState, 'isNewVehicleDiscountActive')
        ) {
          return of(typeOfAgentEmail.vehicleMileageUnder500);
        }
        if (
          this.garageAddressWithMultipleAdvancedPolicies(
            vehicleDetailsState,
            advancedAutoPolicies as AutoPolicy[]
          )
        ) {
          return of(typeOfAgentEmail.vehicleGarageAddressUnknown);
        }
        if (
          this.underWritingHardStopCheck(vehicleDetailsState?.underwritingQuestions) ||
          this.primaryUseHardStopCheck(_get(vehicleDetailsState, 'primaryUsageInfo')) ||
          this.garageAddressWithSingleAdvancedPolicy(
            vehicleDetailsState,
            advancedAutoPolicies as AutoPolicy[]
          ) ||
          _get(driversList, 'length', 0) > 2 * _get(vehicleList, 'length', 0)
        ) {
          return of(typeOfAgentEmail.vehicleHasKitBusinessCoOwnerFlag);
        }
        if (
          vehicleDetailsState?.underwritingQuestions.hasLoanOrLease &&
          !vehicleDetailsState?.tpiInfo?.name
        ) {
          return of(typeOfAgentEmail.vehicleBoundNeedsTPI);
        }

        if (
          !vehicleDetailsState?.underwritingQuestions?.hasLoanOrLease ||
          (vehicleDetailsState?.underwritingQuestions?.hasLoanOrLease &&
            !!vehicleDetailsState?.tpiInfo?.name)
        ) {
          return of(typeOfAgentEmail.vehicleBoundNoTPI);
        }
      })
    );
  }

  typeOfPartnerEmailBeingSent(
    isAddVehicleTransActionCancelled?: boolean
  ): Observable<typeOfAgentEmail> {
    if (isAddVehicleTransActionCancelled) {
      return of(typeOfAgentEmail.vehicleCancelledTransaction);
    }

    return this.store.select(addVehicleFeatureSelector.selectCurrentVehicleInfo).pipe(
      withLatestFrom(
        this.store.select(policyQuery.selectAdvanceAutoPolicies),
        this.vehicleCoveragesFacade.drivers$,
        this.vehicleCoveragesFacade.vehicles$
      ),
      mergeMap(([vehicleDetailsState, advancedAutoPolicies, driversList, vehicleList]) => {
        if (
          !_get(vehicleDetailsState, 'primaryUsageInfo.primaryUse') &&
          !!_get(vehicleDetailsState, 'isNewVehicleDiscountActive')
        ) {
          return of(typeOfAgentEmail.vehicleMileageUnder500);
        }
        if (
          this.garageAddressWithMultipleAdvancedPolicies(
            vehicleDetailsState,
            advancedAutoPolicies as AutoPolicy[]
          )
        ) {
          return of(typeOfAgentEmail.vehicleGarageAddressUnknown);
        }
        if (
          this.underWritingHardStopCheck(vehicleDetailsState?.underwritingQuestions) ||
          this.primaryUseHardStopCheck(_get(vehicleDetailsState, 'primaryUsageInfo')) ||
          this.garageAddressWithSingleAdvancedPolicy(
            vehicleDetailsState,
            advancedAutoPolicies as AutoPolicy[]
          ) ||
          _get(driversList, 'length', 0) > 2 * _get(vehicleList, 'length', 0)
        ) {
          return of(typeOfAgentEmail.vehicleHasKitBusinessCoOwnerFlag);
        }
        if (
          (_get(vehicleDetailsState, 'underwritingQuestions.hasLoanOrLease') ||
            _get(vehicleDetailsState, 'underwritingQuestions.hasLoan') ||
            _get(vehicleDetailsState, 'underwritingQuestions.hasLease')) &&
          !_get(vehicleDetailsState, 'tpiInfo.name')
        ) {
          return of(typeOfAgentEmail.vehicleBoundNeedsTPI);
        }
        if (
          !(
            _get(vehicleDetailsState, 'underwritingQuestions.hasLoanOrLease') ||
            _get(vehicleDetailsState, 'underwritingQuestions.hasLoan') ||
            _get(vehicleDetailsState, 'underwritingQuestions.hasLease')
          ) ||
          !!_get(vehicleDetailsState, 'tpiInfo.name')
        ) {
          return of(typeOfAgentEmail.vehicleBoundNoTPI);
        }
      })
    );
  }

  featureEquipmentsListEntered(
    featuresListFound: string[],
    vehicleFeatures: VehicleFeaturesInfoModel,
    userStateCode: string
  ): string {
    const isAntiTheftEligibleState = !!this.copyService
      .getCopy('policy.newVehicleFeaturesAndEquipmentAntiTheftEligibleStates')
      .find(state => state === userStateCode);

    if (isAntiTheftEligibleState) {
      return featuresListFound.length < 5
        ? this.getEnteredFeaturesList(vehicleFeatures)
        : featuresListFound.join(', ');
    }
    return featuresListFound.length < 4
      ? this.getEnteredFeaturesList(vehicleFeatures)
      : featuresListFound.join(', ');
  }

  getPrimaryDriverOnVehicle(vin: string, driversList: DriverInfoModel[] = []): string {
    return _get(
      driversList.find(driver => driver.vin === vin),
      'name',
      'Not Provided'
    );
  }

  getFullGlassCoverageValue(
    vehicleCoverages: AddVehicleCoverage[] = [],
    userStateCode: string
  ): vehicleCoverageSelectionOptions {
    const fullGlassCoveragecode = this.copyService.getCopy('policy.fullGlassCoverageCode');
    const vehicleCoverageMap: Dictionary<CoverageDescriptionModel> = this.copyService.getCopy(
      'policy.vehicleCoverageCodeMap'
    );
    const { eligibleStates } = vehicleCoverageMap[fullGlassCoveragecode];

    if (!eligibleStates.find(eligibleState => eligibleState === userStateCode)) {
      return vehicleCoverageSelectionOptions.NotApplicable;
    }

    return _get(
      vehicleCoverages.find(
        vehicleCoverage => vehicleCoverage.coverageCode === fullGlassCoveragecode
      ),
      'selected'
    )
      ? vehicleCoverageSelectionOptions.Yes
      : vehicleCoverageSelectionOptions.Rejected;
  }

  getUIMPDCoverageValue(
    vehicleCoverages: AddVehicleCoverage[],
    userStateCode: string
  ): vehicleCoverageSelectionOptions {
    const uimpdCoveragecode = this.copyService.getCopy(
      'policy.underinsuredMotoristPropertyDamageCode'
    );
    const vehicleCoverageMap: Dictionary<CoverageDescriptionModel> = this.copyService.getCopy(
      'policy.vehicleCoverageCodeMap'
    );
    const { eligibleStates } = vehicleCoverageMap[uimpdCoveragecode];

    if (!eligibleStates.find(eligibleState => eligibleState === userStateCode)) {
      return vehicleCoverageSelectionOptions.NotApplicable;
    }

    return _get(
      vehicleCoverages.find(vehicleCoverage => vehicleCoverage.coverageCode === uimpdCoveragecode),
      'terms[0].selectedOption'
    )
      ? vehicleCoverageSelectionOptions.Yes
      : vehicleCoverageSelectionOptions.Rejected;
  }

  getUMPDCoverageValue(
    vehicleCoverages: AddVehicleCoverage[],
    userStateCode: string
  ): vehicleCoverageSelectionOptions {
    const impdGlassCoveragecode = this.copyService.getCopy(
      'policy.uninsuredMotoristPropertyDamage'
    );
    const vehicleCoverageMap: Dictionary<CoverageDescriptionModel> = this.copyService.getCopy(
      'policy.vehicleCoverageCodeMap'
    );
    const { eligibleStates } = vehicleCoverageMap[impdGlassCoveragecode];

    if (!eligibleStates.find(eligibleState => eligibleState === userStateCode)) {
      return vehicleCoverageSelectionOptions.NotApplicable;
    }

    return vehicleCoverages.find(
      vehicleCoverage => vehicleCoverage.coverageCode === impdGlassCoveragecode
    )?.terms[0]?.selectedOption
      ? vehicleCoverageSelectionOptions.Yes
      : vehicleCoverageSelectionOptions.Rejected;
  }

  /**
   * @author Abhishek Singh
   * @param analyticPayload: Payload for sending the analytics.
   * @returns
   * @description Send the analytics for the add vehicle actions.
   */
  public sendAddVehicleAnalytics(analyticPayload: AddVehicleAnalyticsModel): void {
    const pageObj: PageAnalyticParams = {
      pageName: `MyAccount:Policies:AddVehicle:${analyticPayload.pageAnalyticName}`,
      experience: '',
      primaryCategory: 'My Account',
      subCategory1: 'Add Vehicle',
      subCategory2: '',
      subCategory3: ''
    };
    const eventObj: EventAnalyticParams = {
      eventName: 'Add Vehicle',
      eventStep: analyticPayload.eventName,
      replaceVehicle: {
        agentName: analyticPayload.agentName
      }
    };
    if (analyticPayload.buttonAnalyticName) {
      return this.analyticsFacade.trackButtonClick({
        link: `MyAccount:Policies:AddVehicle:${analyticPayload.buttonAnalyticName}`
      });
    }
    if (analyticPayload.eventName) {
      return this.analyticsFacade.trackPageAndEvent(pageObj, eventObj);
    }
    return this.analyticsFacade.trackPage(pageObj);
  }

  public addVehicleHardStopSendAnalytics(analyticPayload: AddVehicleAnalyticsModel): void {
    const pageObj: PageAnalyticParams = {
      pageName: `MyAccount:AddVehicle:HardStop:${analyticPayload.pageAnalyticName}`,
      experience: '',
      primaryCategory: 'My Account',
      subCategory1: 'Hardstops',
      subCategory2: analyticPayload.pageAnalyticName?.includes('ClassicPolicy:SentToAgent')
        ? 'SentToAgent'
        : '',
      subCategory3: ''
    };
    return this.analyticsFacade.trackPage(pageObj);
  }

  public buildAddVehicleRatePayload(
    risk: RiskModel,
    vehicleInfo: VehicleInfo,
    vehicleCoveragesFromInitiate: AddVehicleCoverage[],
    driverAssignments: DriverInfoModel[],
    isReset,
    isPartner,
    submissionId,
    partnerId,
    newVehicle?: NewVehicle
  ): { payload: AddVehicleRateRequest } {
    const vehicle = {
      vin: vehicleInfo.primaryVehicleInfo.vin,
      year: vehicleInfo.primaryVehicleInfo.year,
      make: newVehicle?.make,
      model: newVehicle?.model,
      series: newVehicle?.series,
      currentMileage: parseInt(vehicleInfo.primaryVehicleInfo.odoMeter, 10),
      annualMileage: parseInt(vehicleInfo.primaryUsageInfo.mileage, 10),
      msrp: newVehicle?.estimatedValue || newVehicle?.msrp,
      primaryUsage: vehicleInfo.primaryUsageInfo.primaryUse,
      garageAddress: {
        addressLine1: risk.address.addressLine1,
        city: risk.address.city,
        state: risk.address.state,
        postalCode: risk.address.zipCode,
        country: 'USA'
      },
      airBagsIndicator: vehicleInfo.vehicleFeatures.airBagsIndicator,
      antiLockBrakes: vehicleInfo.vehicleFeatures.antiLockBreaks,
      antiTheftDeviceIndicator: vehicleInfo.vehicleFeatures.antiTheftDeviceIndicator,
      dayTimeRunningLights: vehicleInfo.vehicleFeatures.dayTimeRunningLight,
      electronicStability: vehicleInfo.vehicleFeatures.electronicStabilityControl,
      hasOtherOrCoowner: vehicleInfo.underwritingQuestions.hasOtherOrCoowner,
      tpis: vehicleInfo.tpiInfo ? [vehicleInfo.tpiInfo] : [],
      coverages: isReset
        ? [
            ...vehicleCoveragesFromInitiate.map(vc => ({
              ...vc,
              coverages: vc['coverages'].map(c =>
                c.terms && c.terms.length > 0
                  ? { ...c, updated: true, terms: c.terms.map(t => ({ ...t, updated: true })) }
                  : { ...c, updated: true }
              )
            }))
          ]
        : _get(vehicleInfo, 'vehicleCoverages')
          ? ([
              {
                fixedId: vehicleCoveragesFromInitiate[0]?.fixedId,
                vehicleName: vehicleCoveragesFromInitiate[0]?.vehicleName,
                publicID: vehicleCoveragesFromInitiate[0]?.publicID,
                coverages: _get(vehicleInfo, 'vehicleCoverages')
              }
            ] as unknown as AddVehicleCoverage[])
          : _get(vehicleInfo, 'vehicleCoverages', vehicleCoveragesFromInitiate)
    };
    if (!isPartner) delete vehicle.primaryUsage;
    const currentDate = new Date();
    return {
      payload: {
        policyNumber: risk.policyNumber,
        submissionId: '',
        policyLine: isPartner ? 'AUTO' : undefined,
        contractState: isPartner ? risk.contractState : undefined,
        // This hardcoding needs to change if the add vehicle support motor cycle or home owners.
        policyType: isPartner ? 'FAMILY_CAR' : undefined,
        effectiveDate: isPartner ? currentDate.toISOString() : undefined,
        partnerCode: isPartner ? partnerId : undefined,
        vehicle,
        drivers: driverAssignments
      }
    };
  }

  transformKeyValuPairs(prefix: string, data: QuoteSummaryModel[], hideValue = false) {
    const dataMap = new Map<string, string>();
    for (let i = 0; i < data.length; i++) {
      dataMap.set(`${prefix}${i + 1}`, data[i].option || '');
      if (!hideValue) dataMap.set(`${prefix}${i + 1}Value`, data[i].value || '');
    }
    return dataMap;
  }

  transformVehicleCoverages(coverages) {
    const updatedCoverages = [...coverages];
    const coverageCopyObjList = <Dictionary<CoverageDescriptionModel>>(
      this.copyService.getCopy('policy.vehicleCoverageCodeMap')
    );
    const coverageCodeListFromCopy = Object.keys(coverageCopyObjList);
    const coverageCodeListFromData = _flatMap(coverages, coverage => coverage.coverageCode);
    for (const copyCoverageCode of coverageCodeListFromCopy) {
      if (!coverageCodeListFromData.find(coverageCode => coverageCode === copyCoverageCode)) {
        updatedCoverages.push({
          coverageCode: copyCoverageCode,
          coverageName: '',
          termValue: '',
          selected: false,
          required: false,
          hasTerms: false,
          terms: []
        });
      }
    }

    return updatedCoverages
      .filter(coverage => {
        const coverageCopyObj = coverageCopyObjList[_get(coverage, 'coverageCode')];
        return !!coverageCopyObj;
      })
      .map((coverage): QuoteSummaryModel => {
        const coverageDescriptionObj = coverageCopyObjList[_get(coverage, 'coverageCode')];
        const coverageOptions = _flatMap(_get(coverage, 'terms', []), term => ({
          options: _flatMap(_get(term, 'options'), option => option.name),
          selectedValue: _get(term, 'selectedOption')
        }));
        return {
          option: _get(coverageDescriptionObj, 'description'),
          value:
            !!_get(coverageDescriptionObj, 'isCheckBox') && coverage.selected
              ? 'Included'
              : this.getCoverageValue(
                  coverageOptions,
                  coverageDescriptionObj,
                  coverage.coverageCode
                ),
          isCheckbox: !!_get(coverageDescriptionObj, 'isCheckBox'),
          coverageHelpTextList: this.handleUnderInsuredDeductibleHelpText(coverage)
        };
      })
      .sort((x, y) => (x.isCheckbox === y.isCheckbox ? 0 : x.isCheckbox ? 1 : -1));
  }

  transformPolicyCoverages(policy: AutoPolicy) {
    const coverages = this.autoPolicyService.getPolicyLevelCoverages(<AutoPolicy>policy);
    const coverageModels: QuoteSummaryModel[] = [];
    for (const coverage of coverages) {
      if (
        _get(coverage, 'limitTerms[0].value') !== '' ||
        _get(coverage, 'deductibleTerms[0].value') !== ''
      ) {
        const coverageModel = new CoveragesModel(
          coverage,
          (<Dictionary<CoverageDescriptionModel>>(
            this.copyService.getCopy('policy.policyCoverageCodeMap')
          ))[coverage.code].subHeading
        );
        if (coverageModel.isVisible) {
          coverageModels.push({
            option: coverageModel.coverage.description,
            value: this.getPolicyCoverageLimits(coverageModel)
          });
        }
      }
    }
    return coverageModels;
  }

  transformVehicleDiscounts(discounts) {
    return _flatMap(discounts, appliedDiscount => ({
      option: appliedDiscount.name.replace(/Discount/, ''),
      value: 'Applied'
    }));
  }

  private buildCustomerMessageDetails(
    customerEmailAddress: string,
    customerFirstName: string,
    prodId: string,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: number,
    newTermDescription: number,
    drivers: string,
    garagedAddress: string,
    coverages: string,
    fees: string,
    discounts: string
  ): MessageDetail[] {
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('EmailAddress', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('SubscriberKey', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('FirstName', customerFirstName || ''));
    messageDetails.push(new MessageDetail('ProdID', prodId || ''));
    messageDetails.push(new MessageDetail('PolicyNumber', policyNumber || ''));
    messageDetails.push(new MessageDetail('NewRiskInformation', newRiskInformation || ''));
    messageDetails.push(new MessageDetail('NewTotalPremium', newTotalPremium || null));
    messageDetails.push(new MessageDetail('NewMonthlyPremium', newMonthlyPremium || null));
    messageDetails.push(new MessageDetail('NewTermDescription', newTermDescription || ''));
    messageDetails.push(new MessageDetail('Drivers', drivers || ''));
    messageDetails.push(new MessageDetail('GaragedAddress', garagedAddress || ''));
    messageDetails.push(new MessageDetail('Coverages', coverages || ''));
    messageDetails.push(new MessageDetail('Fees', fees || ''));
    messageDetails.push(new MessageDetail('Discounts', discounts || ''));
    return messageDetails;
  }

  private buildCustomerMessageDetailsAfterBind(
    customerEmailAddress: string,
    customerFirstName: string,
    prodId: string,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: number,
    newTermDescription: number,
    vehicleCoverages: Map<string, string>,
    policyCoverages: Map<string, string>,
    discounts: Map<string, string>,
    futurePayments: Map<string, string>,
    vehicleCount: number
  ): MessageDetail[] {
    const dollarUS = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('EmailAddress', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('SubscriberKey', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('FirstName', customerFirstName || ''));
    messageDetails.push(new MessageDetail('ProdID', prodId || ''));
    messageDetails.push(new MessageDetail('PolicyNumber', policyNumber || ''));
    messageDetails.push(new MessageDetail('RiskInformation', newRiskInformation || ''));
    messageDetails.push(
      new MessageDetail('TotalPremium', newTotalPremium ? dollarUS.format(newTotalPremium) : null)
    );
    messageDetails.push(
      new MessageDetail(
        'MonthlyPremium',
        newMonthlyPremium ? dollarUS.format(newMonthlyPremium) : null
      )
    );
    messageDetails.push(new MessageDetail('TermDescription', `${newTermDescription || ''} mos`));
    messageDetails.push(new MessageDetail('VehicleCount', vehicleCount));
    messageDetails.push(...this.buildMessageFromMap(vehicleCoverages));
    messageDetails.push(...this.buildMessageFromMap(policyCoverages));
    messageDetails.push(...this.buildMessageFromMap(discounts));
    messageDetails.push(...this.buildMessageFromMap(futurePayments));

    if (futurePayments && futurePayments.size > 0)
      messageDetails.push(
        new MessageDetail('PaymentFrequency', futurePayments.size > 2 ? 'Monthly' : 'FullPay')
      );
    return messageDetails;
  }

  private buildMessageFromMap(data: Map<string, string>) {
    const messageDetails: MessageDetail[] = [];
    data.forEach(function (value, key) {
      messageDetails.push(new MessageDetail(key, value));
    });
    return messageDetails;
  }

  private buildPartnerCustomerMessageDetails(
    customerEmailAddress: string,
    customerFirstName: string,
    policyNumber: string,
    newRiskInformation: string,
    newTotalPremium: number,
    newMonthlyPremium: string,
    newTermDescription: number,
    drivers: string,
    garagedAddress: string,
    coverages: string,
    fees: string,
    discounts: string
  ): MessageDetail[] {
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('EmailAddress', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('SubscriberKey', customerEmailAddress || ''));
    messageDetails.push(new MessageDetail('FirstName', customerFirstName || ''));
    messageDetails.push(new MessageDetail('PolicyNumber', policyNumber || ''));
    messageDetails.push(new MessageDetail('NewRiskInformation', newRiskInformation || ''));
    messageDetails.push(new MessageDetail('NewTotalPremium', newTotalPremium || null));
    messageDetails.push(new MessageDetail('NewMonthlyPremium', newMonthlyPremium || null));
    messageDetails.push(new MessageDetail('NewTermDescription', newTermDescription || ''));
    messageDetails.push(new MessageDetail('Drivers', drivers || ''));
    messageDetails.push(new MessageDetail('GaragedAddress', garagedAddress || ''));
    messageDetails.push(new MessageDetail('Coverages', coverages || ''));
    messageDetails.push(new MessageDetail('Fees', fees || ''));
    messageDetails.push(new MessageDetail('Discounts', discounts || ''));
    return messageDetails;
  }

  private garageAddressHardStopCheck(garageAddress: string) {
    return (
      garageAddress === this.copyService.getCopy('policy.addVehicleGarageSomewhereElseOptionText')
    );
  }

  private getEnteredFeaturesList(vehicleFeatures: VehicleFeaturesInfoModel) {
    let featuresList = '';
    if (_get(vehicleFeatures, 'airBagsIndicator')) {
      featuresList += `Airbags -${vehicleFeatures.airBagsIndicator}`;
    }
    if (_get(vehicleFeatures, 'antiLockBreaks')) {
      featuresList += ', Anti-lock brakes';
    }
    if (_get(vehicleFeatures, 'antiTheftDeviceIndicator')) {
      featuresList += `, Anti-Theft Device -${vehicleFeatures.antiTheftDeviceIndicator}`;
    }
    if (_get(vehicleFeatures, 'dayTimeRunningLight')) {
      featuresList += ', Daytime running lights';
    }
    if (_get(vehicleFeatures, 'electronicStabilityControl')) {
      featuresList += ', Electronic stability control';
    }
    return featuresList;
  }

  private handleUnderInsuredDeductibleHelpText(coverage) {
    const underInsuredHelpTextObj: string[] = [];

    for (const term of coverage.terms) {
      if (term.name === this.copyService.getCopy('policy.hit-and-RunPhantomVehicleDamage')) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.coverageCode}]['coverageHelpText1']`,
            {
              hitdeductible: term.selectedOption
            }
          )
        );
      } else if (
        term.name === this.copyService.getCopy('policy.notAHit-and-RunPhantomVehicleDamage')
      ) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.coverageCode}]['coverageHelpText2']`,
            {
              noHitdeductible: term.selectedOption
            }
          )
        );
      }
    }

    return underInsuredHelpTextObj;
  }

  private getVehicleCoverageLimits(value: string, coverageCode: string) {
    if (!value) {
      return 'Not Included';
    }
    return new CoverageCurrencyUSDPipe().transform(value, coverageCode);
  }

  private isAntiTheftDiscountCode(appliedDiscount) {
    return appliedDiscount.discountCode === 'PAAntiTheftDisc_af';
  }

  private getCoverageValue(
    coverageOptions: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options: any[];
      selectedValue: string;
    }[],
    coverageDescriptionObj: CoverageDescriptionModel,
    coverageCode: string
  ) {
    if (_has(coverageDescriptionObj, 'isCheckBox')) {
      return _get(coverageOptions[0], 'selectedValue') ? 'Included' : 'Not Included';
    }
    if (coverageOptions.length > 1) {
      return (
        this.getVehicleCoverageLimits(_get(coverageOptions[0], 'selectedValue'), coverageCode) +
        ', ' +
        this.getVehicleCoverageLimits(_get(coverageOptions[1], 'selectedValue'), coverageCode)
      );
    }
    return this.getVehicleCoverageLimits(_get(coverageOptions[0], 'selectedValue'), coverageCode);
  }

  private getPolicyCoverageLimits(model: CoveragesModel) {
    let limit = '';
    for (const term of model.coverage.limitTerms) {
      limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
    }
    if (!!model?.isStandardCoverage && !(model?.isImpliedCoverage || model?.isLiabilityCoverage)) {
      for (const term of model.coverage.deductibleTerms) {
        limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
      }
    }

    return limit;
  }
}
